import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuthStore } from '@/store/authStore';
import { PaywallModal } from './PaywallModal';
import { CreditCard, Calendar, AlertTriangle, CheckCircle, Loader2, Crown } from 'lucide-react';
import { format } from 'date-fns';

export function SubscriptionSettings() {
  const {
    status,
    isLoading,
    error,
    hasAccess,
    trialDaysRemaining,
    isTrialing,
    isPastDue,
    isCancelled,
    isActive,
    tier,
    manageSubscription
  } = useSubscription();

  const { user } = useAuthStore();
  const [showPaywall, setShowPaywall] = useState(false);
  const [isManaging, setIsManaging] = useState(false);

  const handleManageSubscription = async () => {
    setIsManaging(true);
    await manageSubscription();
    setIsManaging(false);
  };

  // Pioneers don't need the subscription management section
  // Check both hook tier and auth store directly to avoid flash during initial load
  const isPioneer = tier === 'pioneer' || user?.subscription?.plan === 'pioneer' || user?.subscription?.isPioneer;
  if (isPioneer) {
    return null;
  }

  if (isLoading && !status) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-muted-foreground">Loading subscription...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = () => {
    if (!status) return null;

    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; text: string }> = {
      active: { variant: 'default', text: 'Active' },
      trialing: { variant: 'secondary', text: `Trial (${trialDaysRemaining} days left)` },
      past_due: { variant: 'destructive', text: 'Past Due' },
      cancelled: { variant: 'outline', text: 'Cancelled' },
      inactive: { variant: 'outline', text: 'Inactive' },
    };

    const statusKey = status.status || 'inactive';
    const { variant, text } = variants[statusKey] || variants.inactive;
    return <Badge variant={variant}>{text}</Badge>;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription
          </CardTitle>
          <CardDescription>
            Manage your GodMode Pro subscription
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status</span>
            <div className="flex items-center gap-2">
              {hasAccess ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <div className="h-4 w-4" /> // Placeholder for alignment
              )}
              {getStatusBadge()}
            </div>
          </div>

          {/* Plan */}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Plan</span>
            <span className="font-medium capitalize flex items-center gap-1">
              {tier === 'pro' && <Crown className="h-4 w-4 text-yellow-500" />}
              {tier || 'Free'}
            </span>
          </div>

          {/* Trial/Renewal Info */}
          {isTrialing && status?.trialEndsAt && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Trial ends</span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(new Date(status.trialEndsAt), 'MMM d, yyyy')}
              </span>
            </div>
          )}

          {status?.currentPeriodEnd && isActive && !isCancelled && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Renews</span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(new Date(status.currentPeriodEnd), 'MMM d, yyyy')}
              </span>
            </div>
          )}

          {/* Past Due Warning */}
          {isPastDue && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Your payment failed. Please update your payment method to continue using Pro features.
              </span>
            </div>
          )}

          {/* Cancelled Notice */}
          {isCancelled && status?.currentPeriodEnd && (
            <div className="bg-muted p-3 rounded-md text-sm">
              Your subscription will end on {format(new Date(status.currentPeriodEnd), 'MMM d, yyyy')}.
              You'll retain access until then.
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 space-y-2">
            {hasAccess ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleManageSubscription}
                disabled={isManaging}
              >
                {isManaging ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Opening portal...
                  </>
                ) : (
                  'Manage Subscription'
                )}
              </Button>
            ) : (
              <Button
                className="w-full"
                onClick={() => setShowPaywall(true)}
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade to Pro
              </Button>
            )}
          </div>

          {/* Free tier info */}
          {!hasAccess && (
            <div className="pt-2 text-center">
              <p className="text-xs text-muted-foreground">
                Start your 14-day free trial today. £3.99/month after trial.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
      />
    </>
  );
}
